import { Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { repositoryDelivery } from "../../../../_base/_const/_constVar";
import ImportExcelShell from "../../../../common/ImportExcel/ImportExcelShell";
import {
  ImportExcelColumn,
  ImportExcelRowErrors,
  ImportExcelSelectOption,
} from "../../../../common/ImportExcel/ImportExcelTypes";
import {
  parseExcelBoolean,
  toImportSubmitResult,
} from "../../../../common/ImportExcel/importExcelHelper";
import {
  getCommuneSelectNew,
  getProvinceSelectNew,
} from "../../../../service/getSelectApi";

/** Chuẩn hoá tên tỉnh/phường để so khớp không phân biệt hoa thường, dấu cách */
const normalizeName = (value: any) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const sampleRows = [
  {
    code: "99",
    channelcode: "HAC99",
    name: "HACOM Chi nhánh mẫu",
    companyCode: "DVT",
    phone: "02471234567",
    attribute1: "19001903",
    attribute11: "",
    provinceId: "Thành phố Hà Nội",
    communeId: "Phường Yên Hoà",
    attribute10: "Số 1 Trần Duy Hưng",
    attribute2: "99",
    isonline: "Đang sử dụng",
  },
];

const ImportExcelView = ({ onSuccess }: { onSuccess: () => void }) => {
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);
  const provincesRef = useRef<ImportExcelSelectOption[]>([]);
  /** Cache phường/xã theo id tỉnh để không phải gọi api nhiều lần */
  const communeCacheRef = useRef<Map<string, ImportExcelSelectOption[]>>(
    new Map()
  );
  const communeLoadingRef = useRef<
    Map<string, Promise<ImportExcelSelectOption[]>>
  >(new Map());

  const fetchProvinces = async () => {
    try {
      const res = await getProvinceSelectNew();
      provincesRef.current = (res ?? [])
        .filter((item: any) => item.id && item.name)
        .map((item: any) => ({
          value: String(item.id),
          label: String(item.name),
        }));
    } finally {
      setLoadingReferenceData(false);
    }
  };

  useEffect(() => {
    fetchProvinces();
  }, []);

  const loadCommunes = (
    provinceId: string
  ): Promise<ImportExcelSelectOption[]> => {
    const cached = communeCacheRef.current.get(provinceId);
    if (cached) return Promise.resolve(cached);

    const loading = communeLoadingRef.current.get(provinceId);
    if (loading) return loading;

    const promise = getCommuneSelectNew(provinceId)
      .then((res) => {
        const options = (res ?? [])
          .filter((item: any) => item.value && item.text)
          .map((item: any) => ({
            value: String(item.value),
            label: String(item.text),
          }));
        // Chỉ cache khi api trả về dữ liệu, để lần sửa sau còn gọi lại được
        if (options.length > 0) communeCacheRef.current.set(provinceId, options);
        return options;
      })
      .finally(() => communeLoadingRef.current.delete(provinceId));

    communeLoadingRef.current.set(provinceId, promise);
    return promise;
  };

  /**
   * Cột "Tỉnh/TP" và "Phường/Xã" trên file excel nhập theo TÊN, nhưng trên bảng
   * xem trước là ô chọn (select) và giá trị lưu là ID của danh mục địa chỉ.
   * Tên đọc từ excel chỉ dùng để dò ra id và để báo lỗi khi không khớp.
   */
  const columns = useMemo<ImportExcelColumn[]>(
    () => [
      { key: "code", header: "Mã chi nhánh", required: true, size: 130 },
      { key: "name", header: "Tên chi nhánh", required: true, size: 230 },
      {
        key: "channelcode",
        header: "Mã chi nhánh Asia",
        size: 170,
        description: "Mã chi nhánh (mã công ty) bên ERP Asia, không bắt buộc",
      },
      {
        key: "companyCode",
        header: "Công ty",
        required: true,
        type: "select",
        size: 140,
        options: [
          { value: "HACOM", label: "HACOM" },
          { value: "DVT", label: "DVT" },
        ],
        description: "HACOM hoặc DVT, để trống sẽ lấy DVT",
      },
      {
        key: "attribute1",
        header: "SĐT bảo hành",
        required: true,
        size: 140,
        description: "Từ 8 ký tự",
      },
      { key: "phone", header: "SĐT chi nhánh", size: 140 },
      { key: "attribute11", header: "SĐT chi nhánh dùng cho GV", size: 180 },
      {
        key: "provinceId",
        header: "Tỉnh/TP",
        required: true,
        type: "select",
        size: 210,
        placeholder: "Chọn tỉnh/TP",
        options: () => provincesRef.current,
        description:
          "Nhập tên tỉnh/thành phố, hệ thống dò ra danh mục và cho chọn lại nếu không khớp",
      },
      {
        key: "communeId",
        header: "Phường/Xã",
        required: true,
        type: "select",
        size: 210,
        placeholder: "Chọn phường/xã",
        // Phường/xã phụ thuộc tỉnh/TP đang chọn của chính dòng đó
        options: (row) =>
          row.provinceId
            ? communeCacheRef.current.get(String(row.provinceId)) ?? []
            : [],
        description:
          "Nhập tên phường/xã thuộc tỉnh/TP ở trên, có thể chọn lại nếu không khớp",
      },
      { key: "attribute10", header: "Địa chỉ cụ thể", size: 220 },
      {
        key: "address",
        header: "Địa chỉ (tự ghép)",
        size: 300,
        inTemplate: false,
        editable: false,
        description: "Ghép từ địa chỉ cụ thể - phường/xã - tỉnh/TP",
      },
      {
        key: "attribute2",
        header: "STT hiển thị",
        size: 110,
        description: "Số thứ tự hiển thị của chi nhánh",
      },
      {
        key: "isonline",
        header: "Sử dụng",
        type: "boolean",
        size: 150,
        description: "Đang sử dụng | Dừng sử dụng, để trống sẽ lấy Đang sử dụng",
      },
    ],
    []
  );

  const mapRow = useCallback((raw: Record<string, any>) => {
    // Giá trị đọc từ excel của 2 cột địa chỉ là TÊN, giữ lại để dò và để báo lỗi
    const provinceRawName = String(raw.provinceId ?? "").trim();
    const communeRawName = String(raw.communeId ?? "").trim();
    const province = provincesRef.current.find(
      (p) => normalizeName(p.label) === normalizeName(provinceRawName)
    );

    return {
      code: raw.code ?? "",
      channelcode: raw.channelcode ?? "",
      name: raw.name ?? "",
      companyCode: (raw.companyCode ?? "").toUpperCase() || "DVT",
      phone: raw.phone ?? "",
      attribute1: raw.attribute1 ?? "",
      attribute11: raw.attribute11 ?? "",
      attribute10: raw.attribute10 ?? "",
      attribute2: raw.attribute2 ?? "",
      isonline: parseExcelBoolean(raw.isonline, true),
      provinceRawName,
      communeRawName,
      provinceId: province?.value ?? null,
      communeId: null as string | null,
      address: "",
    };
  }, []);

  const resolveRow = useCallback(async (row: Record<string, any>) => {
    const province = provincesRef.current.find(
      (p) => String(p.value) === String(row.provinceId)
    );

    let communes: ImportExcelSelectOption[] = [];
    if (province) communes = await loadCommunes(province.value);

    let communeId = row.communeId ? String(row.communeId) : null;

    // Đổi tỉnh/TP thì phường/xã cũ không còn hợp lệ
    if (communeId && !communes.some((c) => String(c.value) === communeId))
      communeId = null;

    // Chưa có phường/xã thì dò theo tên đọc từ excel
    if (!communeId && normalizeName(row.communeRawName).length > 0) {
      const matched = communes.find(
        (c) => normalizeName(c.label) === normalizeName(row.communeRawName)
      );
      communeId = matched?.value ?? null;
    }

    const commune = communes.find((c) => String(c.value) === communeId);

    const address = [
      String(row.attribute10 ?? "").trim(),
      commune?.label ?? "",
      province?.label ?? "",
    ]
      .filter((i) => i && i.length > 0)
      .join(" - ");

    return {
      ...row,
      provinceId: province?.value ?? null,
      communeId,
      address,
    };
  }, []);

  const validateRows = useCallback(
    (rows: Record<string, any>[]): ImportExcelRowErrors => {
      const errors: ImportExcelRowErrors = {};
      const codeSeen = new Map<string, number>();
      const nameSeen = new Map<string, number>();

      const addError = (index: number, key: string, message: string) => {
        if (!errors[index]) errors[index] = {};
        if (!errors[index][key]) errors[index][key] = message;
      };

      rows.forEach((row, index) => {
        const code = String(row.code ?? "").trim();
        const name = String(row.name ?? "").trim();

        if (code.length === 0) addError(index, "code", "Chưa nhập mã chi nhánh");
        else if (code.length > 15)
          addError(index, "code", "Mã chi nhánh tối đa 15 ký tự");
        else {
          const first = codeSeen.get(code.toLowerCase());
          if (first !== undefined)
            addError(index, "code", `Trùng mã với dòng ${first + 1}`);
          else codeSeen.set(code.toLowerCase(), index);
        }

        if (name.length === 0) addError(index, "name", "Chưa nhập tên chi nhánh");
        else if (name.length > 150)
          addError(index, "name", "Tên chi nhánh tối đa 150 ký tự");
        else {
          const first = nameSeen.get(name.toLowerCase());
          if (first !== undefined)
            addError(index, "name", `Trùng tên với dòng ${first + 1}`);
          else nameSeen.set(name.toLowerCase(), index);
        }

        if (String(row.channelcode ?? "").trim().length > 250)
          addError(index, "channelcode", "Mã chi nhánh Asia tối đa 250 ký tự");
        // chi nhánh thuộc công ty DVT bắt buộc phải có mã bên ERP Asia
        else if (
          String(row.companyCode ?? "") === "DVT" &&
          String(row.channelcode ?? "").trim().length === 0
        )
          addError(
            index,
            "channelcode",
            "Chi nhánh thuộc công ty DVT phải nhập mã chi nhánh Asia"
          );

        if (!["HACOM", "DVT"].includes(String(row.companyCode ?? "")))
          addError(index, "companyCode", "Công ty phải là HACOM hoặc DVT");

        const warrantyPhone = String(row.attribute1 ?? "").trim();
        if (warrantyPhone.length < 8)
          addError(index, "attribute1", "SĐT bảo hành phải từ 8 ký tự");

        if (!row.provinceId)
          addError(
            index,
            "provinceId",
            String(row.provinceRawName ?? "").length > 0
              ? `Không khớp tỉnh/TP "${row.provinceRawName}", vui lòng chọn lại`
              : "Chưa chọn tỉnh/TP"
          );

        if (!row.communeId)
          addError(
            index,
            "communeId",
            String(row.communeRawName ?? "").length > 0 && row.provinceId
              ? `Không khớp phường/xã "${row.communeRawName}", vui lòng chọn lại`
              : "Chưa chọn phường/xã"
          );
      });

      return errors;
    },
    []
  );

  const onSubmit = useCallback(async (rows: Record<string, any>[]) => {
    const payload = rows.map((row) => ({
      id: 0,
      code: String(row.code ?? "").trim(),
      name: String(row.name ?? "").trim(),
      address: row.address || null,
      phone: String(row.phone ?? "").trim() || null,
      fax: null,
      email: null,
      note: null,
      location: null,
      road: null,
      province: null,
      country: null,
      // mã chi nhánh trên hệ thống ERP Asia
      channelcode: String(row.channelcode ?? "").trim() || null,
      // isonline là cờ đang sử dụng của chi nhánh (1 = đang sử dụng)
      isonline: row.isonline ? 1 : 0,
      masterInventoryOrg: null,
      guarantee: null,
      orgId: null,
      townName: null,
      provinceId: null,
      companyCode: row.companyCode,
      attribute1: String(row.attribute1 ?? "").trim() || null,
      attribute2: String(row.attribute2 ?? "").trim() || null,
      attribute3: row.address || null,
      attribute4: null,
      attribute5: null,
      attribute6: null,
      attribute7: null,
      // gửi xuống api chỉ là id của tỉnh/TP và phường/xã
      attribute8: row.provinceId,
      attribute9: row.communeId,
      attribute10: String(row.attribute10 ?? "").trim() || null,
      attribute11: String(row.attribute11 ?? "").trim() || null,
    }));

    const response = await repositoryDelivery.post(
      "/api/v1/TblDmMasterOrg/create-list",
      payload
    );

    return toImportSubmitResult(response);
  }, []);

  return (
    <ImportExcelShell
      columns={columns}
      templateFileName="mau-import-danh-muc-chi-nhanh.xlsx"
      sampleRows={sampleRows}
      mapRow={mapRow}
      resolveRow={resolveRow}
      validateRows={validateRows}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      loadingReferenceData={loadingReferenceData}
      hint={
        <Text size="xs">
          Cột <b>Tỉnh/TP</b> và <b>Phường/Xã</b> nhập theo tên, hệ thống sẽ dò
          sang danh mục địa chỉ để lấy id. Dòng nào không khớp sẽ báo đỏ và bạn
          chọn lại ngay trên bảng xem trước. Chi nhánh thuộc công ty{" "}
          <b>DVT</b> bắt buộc phải có <b>Mã chi nhánh Asia</b>.
        </Text>
      }
    />
  );
};

export default ImportExcelView;
